/**
 * Agent Talktime panel.
 *
 * Shows per-agent call performance for the HOD: total / connected
 * calls, incoming answered & missed, outgoing duration, etc., over
 * a rolling window (default 7 days). The HOD scans this every
 * morning to spot agents who aren't picking up or aren't dialing.
 *
 * Backed by GET /api/hod/agent-talktime which already does all the
 * grouping server-side; the panel just renders + filters.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Skeleton } from './ui/skeleton';
import { PhoneCall, Lightbulb, Calendar, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Render a duration in HH:MM:SS — matches the columns in the
// screenshot. Anything under a minute still shows as 00:00:NN so the
// row width stays steady.
const fmtDuration = (seconds) => {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  return [hh, mm, ss].map((v) => String(v).padStart(2, '0')).join(':');
};

// Cheap deterministic avatar background so two-letter initials feel
// like distinct people without dragging in a dependency.
const initialsBg = (name) => {
  const palette = [
    'bg-slate-200 text-slate-700',
    'bg-violet-200 text-violet-700',
    'bg-amber-200 text-amber-700',
    'bg-emerald-200 text-emerald-700',
    'bg-rose-200 text-rose-700',
    'bg-blue-200 text-blue-700',
  ];
  let hash = 0;
  for (const c of (name || '')) hash = (hash + c.charCodeAt(0)) % palette.length;
  return palette[hash];
};

const initials = (name) => {
  const parts = (name || '').trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Friendly label for the role chip beneath an agent's name. Falls
// back to "Agent" so the row never looks unlabeled.
const prettyRole = (role) => {
  if (!role) return 'Agent';
  return String(role)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

export default function AgentTalktimePanel() {
  const { token } = useAuth();
  const [recordType, setRecordType] = useState('lead');
  const [days, setDays]             = useState(7);
  const [agents, setAgents]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [updatedAt, setUpdatedAt]   = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/hod/agent-talktime`, {
        params: { token, record_type: recordType, days },
      });
      setAgents(r.data?.data?.agents || []);
      setUpdatedAt(r.data?.data?.as_of);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load Agent Talktime');
    } finally {
      setLoading(false);
    }
  }, [token, recordType, days]);
  useEffect(() => { load(); }, [load]);

  // Lightweight derived stats so we can drop a one-line "insight" at
  // the top — fastest / slowest / quietest agent.
  const insights = useMemo(() => {
    if (agents.length === 0) return null;
    const withCalls = agents.filter((a) => a.total_calls > 0);
    if (withCalls.length === 0) {
      return { top: null, quiet: agents[0] };
    }
    const top   = [...withCalls].sort((a, b) =>
      b.total_duration - a.total_duration)[0];
    const quiet = [...agents].sort((a, b) =>
      a.total_calls - b.total_calls)[0];
    return { top, quiet };
  }, [agents]);

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-3 flex flex-row items-start justify-between gap-3 flex-wrap">
        <div>
          <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <PhoneCall size={16} className="text-slate-500" />
            Agent Talktime
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Monitor Agent Call Performance — Max / Min Talk Time
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={recordType} onValueChange={setRecordType}>
            <SelectTrigger className="w-[110px] h-8 text-xs">
              <Lightbulb size={12} className="mr-1.5 text-amber-500" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="lead">Lead</SelectItem>
              <SelectItem value="deal">Deal</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v, 10))}>
            <SelectTrigger className="w-[140px] h-8 text-xs">
              <Calendar size={12} className="mr-1.5 text-slate-500" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Today</SelectItem>
              <SelectItem value="7">Last 7 Days</SelectItem>
              <SelectItem value="14">Last 14 Days</SelectItem>
              <SelectItem value="30">Last 30 Days</SelectItem>
              <SelectItem value="90">Last 90 Days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" className="h-8 px-2"
                  onClick={load} disabled={loading} title="Refresh">
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
      </CardHeader>

      <CardContent>
        {/* Insight strip — one-line punchline above the grid so the
            HOD doesn't need to scan every row. */}
        {insights?.top && (
          <p className="text-xs text-slate-500 mb-3">
            Most talktime: <span className="font-semibold text-slate-800">{insights.top.agent_name}</span>{' '}
            ({fmtDuration(insights.top.total_duration)}).{' '}
            {insights.quiet?.total_calls === 0
              ? <>Idle this window: <span className="font-semibold text-slate-800">{insights.quiet.agent_name}</span>.</>
              : null}
          </p>
        )}

        {loading && agents.length === 0 ? (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : agents.length === 0 ? (
          <p className="text-sm text-slate-500 py-4 text-center">
            No agents or calls found in this window.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="text-left py-2 pr-4 font-medium">Agent Name</th>
                  <th className="text-left py-2 pr-4 font-medium">Total Calls</th>
                  <th className="text-left py-2 pr-4 font-medium">Connected Calls</th>
                  <th className="text-left py-2 pr-4 font-medium">Total Duration</th>
                  <th className="text-left py-2 pr-4 font-medium">Incoming Calls</th>
                  <th className="text-left py-2 pr-4 font-medium" title="Incoming Answered">Incoming Ans...</th>
                  <th className="text-left py-2 pr-4 font-medium" title="Incoming Missed">Incoming Miss...</th>
                  <th className="text-left py-2 pr-4 font-medium" title="Incoming Duration">Incoming Dur...</th>
                  <th className="text-left py-2 pr-4 font-medium">Outgoing Calls</th>
                  <th className="text-left py-2 pr-4 font-medium" title="Outgoing Duration">Outgoing Dura...</th>
                  <th className="text-left py-2 pr-4 font-medium" title="Outgoing Answered">Outgoing Ans...</th>
                </tr>
              </thead>
              <tbody>
                {agents.map((a) => (
                  <tr key={a.agent_id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60">
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-3">
                        <Avatar className="w-8 h-8">
                          <AvatarFallback className={`text-[11px] font-semibold ${initialsBg(a.agent_name)}`}>
                            {initials(a.agent_name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900 truncate">{a.agent_name || '—'}</p>
                          <p className="text-[11px] text-slate-500 truncate">{prettyRole(a.agent_role)}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 pr-4 text-slate-800">{a.total_calls}</td>
                    <td className="py-3 pr-4 text-slate-800">{a.connected_calls}</td>
                    <td className="py-3 pr-4 text-slate-800 font-mono text-xs">{fmtDuration(a.total_duration)}</td>
                    <td className="py-3 pr-4 text-slate-800">{a.incoming_calls}</td>
                    <td className="py-3 pr-4 text-slate-800">{a.incoming_answered}</td>
                    <td className={`py-3 pr-4 ${a.incoming_missed > 0 ? 'text-red-600 font-medium' : 'text-slate-800'}`}>
                      {a.incoming_missed}
                    </td>
                    <td className="py-3 pr-4 text-slate-800 font-mono text-xs">{fmtDuration(a.incoming_duration)}</td>
                    <td className="py-3 pr-4 text-slate-800">{a.outgoing_calls}</td>
                    <td className="py-3 pr-4 text-slate-800 font-mono text-xs">{fmtDuration(a.outgoing_duration)}</td>
                    <td className="py-3 pr-4 text-slate-800">{a.outgoing_answered}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {updatedAt && (
          <p className="text-[11px] text-slate-400 mt-3">
            Updated {new Date(updatedAt).toLocaleString('en-AE', { timeZone: 'Asia/Dubai' })}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
